import { IApiAxiosError } from "@/api";
import { createIndustry, IIndustryCreate } from "@/api/industries";
import { getPolicyTypesUnpaginated, IPolicyType } from "@/api/policy-types";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Autocomplete, Box, Chip, Stack, Typography } from "@mui/material";
import MDEditor from "@uiw/react-md-editor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import React, { useState } from "react";
import rehypeSanitize from "rehype-sanitize";

export const NewIndustryDialog = ({ open, onClose }: DialogProps) => {
    const queryClient = useQueryClient();
    const [name, setName] = useState("");
    const [naic, setNaic] = useState("");
    const [insuranceRisks, setInsuranceRisks] = useState("");
    const [selectedPolicyTypes, setSelectedPolicyTypes] = useState<
        IPolicyType[]
    >([]);

    const { data: policyTypes = [], isPending: isPolicyTypesLoading } =
        useQuery({
            queryKey: ["policy-types-all"],
            queryFn: getPolicyTypesUnpaginated,
        });

    const createMutation = useMutation({
        mutationFn: (data: IIndustryCreate) => createIndustry(data),
        onSuccess: () => {
            handleClose();
            queryClient.invalidateQueries({ queryKey: ["admin-industries"] });
        },
    });

    const handleCreate = () => {
        if (!name.trim() || !naic.trim()) return;

        createMutation.mutate({
            name: name.trim(),
            naic: naic.trim(),
            insurance_risks: insuranceRisks.trim() || null,
            policy_type_ids: selectedPolicyTypes.map((pt) => pt.id),
        });
    };

    const handleClose = () => {
        onClose();
        setName("");
        setNaic("");
        setInsuranceRisks("");
        setSelectedPolicyTypes([]);
        createMutation.reset();
    };

    const axiosError = createMutation.error as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Create New Industry"
            maxWidth="md"
            isLoading={createMutation.isPending}
            actions={[
                {
                    label: "Create",
                    onClick: handleCreate,
                    variant: "contained",
                    color: "primary",
                    disabled:
                        !name.trim() ||
                        !naic.trim() ||
                        createMutation.isPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data.detail ||
                            "An error occurred while creating the industry."}
                    </Typography>
                )
            }
        >
            <Stack spacing={3}>
                <TextField
                    label="Name"
                    error={!!axiosError}
                    value={name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setName(e.target.value)
                    }
                    inputProps={{ autoFocus: true }}
                    required
                    sx={{ width: "100%" }}
                />
                <TextField
                    label="NAIC Code"
                    error={!!axiosError}
                    value={naic}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setNaic(e.target.value)
                    }
                    inputProps={{ maxLength: 6 }}
                    helperText="Max 6 characters (e.g., 11, 31-33)"
                    required
                    sx={{ width: "100%" }}
                />

                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={policyTypes}
                    loading={isPolicyTypesLoading}
                    value={selectedPolicyTypes}
                    onChange={(_, newValue) => setSelectedPolicyTypes(newValue)}
                    getOptionLabel={(option) =>
                        `${option.name} (${option.short_name})`
                    }
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Policy Types"
                            placeholder="Select policy types..."
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    label={option.short_name}
                                    size="small"
                                    {...tagProps}
                                />
                            );
                        })
                    }
                    filterSelectedOptions
                    size="small"
                />

                <Box>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 1 }}
                    >
                        Insurance Risks
                    </Typography>
                    <Box data-color-mode="light">
                        <MDEditor
                            value={insuranceRisks}
                            onChange={(val) => setInsuranceRisks(val || "")}
                            preview="edit"
                            previewOptions={{
                                rehypePlugins: [[rehypeSanitize]],
                            }}
                            height={200}
                        />
                    </Box>
                </Box>
            </Stack>
        </Modal>
    );
};
